import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router";
import { ArrowRight, Globe, Heart, Lock } from "lucide-react";
import { Blue2Page, type NavCta, type Tone } from "../Page";
import { CHELSEA, ME, PLANS, ROLES, can, type Post, type Role } from "./sample";
import "./Members.css";

/* Shared frame for the in-house club prototype at /blue-2/members. The club
   lives behind a members' sub-nav under the normal blue-2 header. A review bar
   switches who's looking (visitor, Thread Friend, member, Chelsea) so each
   page's locked and creator states can be checked; ?as=<role> sets it.

   This file also holds the pieces every club page shares, so the pages agree:
   TONE (header colors), joinCta / JoinButton (join copy), Locked / LockedRow
   (locked states), VisBadge (who can see a post), ReviewControls (page-level
   review switches that live in the bottom review bar), and the .mb-btn and
   .mb-headsm classes in Members.css. Sample data and ME live in sample.ts. */

/** Base path of the club prototype. */
export const MEMBERS_BASE = "/blue-2/members";
/** Club routes. `settings` is an alias of `account` (the page is becoming full member settings). */
export const M = {
  home: MEMBERS_BASE, posts: `${MEMBERS_BASE}/posts`, classes: `${MEMBERS_BASE}/classes`,
  live: `${MEMBERS_BASE}/live`, community: `${MEMBERS_BASE}/community`, account: `${MEMBERS_BASE}/account`,
  settings: `${MEMBERS_BASE}/account`, join: `${MEMBERS_BASE}/join`,
  post: (id: string) => `${MEMBERS_BASE}/posts/${id}`, klass: (id: string) => `${MEMBERS_BASE}/classes/${id}`,
} as const;

/** Header tone for each club page, so neighbouring pages never open on the same color.
    Usage: `<MembersPage page="classes" …>` (or `tone={TONE.classes}`). */
export const TONE = {
  home: "teal", posts: "blue", post: "blue", classes: "gold", klass: "teal",
  live: "coral", community: "blush", account: "periwinkle", settings: "periwinkle", join: "gold",
} as const satisfies Record<string, Tone>;
export type ClubPage = keyof typeof TONE;

const RoleContext = createContext<Role>("member");
/** The review role currently being previewed (visitor, friend, member, chelsea). */
export const useRole = () => useContext(RoleContext);

/** Adds ?as=<role> to a club path (keeping any #hash), so links keep the review role. */
export function withRole(path: string, role: Role) {
  const [p, hash] = path.split("#");
  return `${p}${p.includes("?") ? "&" : "?"}as=${role}${hash ? `#${hash}` : ""}`;
}
/** Hook form of withRole: `const to = useTo(); <Link to={to(M.classes)}>`. */
export function useTo() { const role = useRole(); return (path: string) => withRole(path, role); }

/* ─── Join copy ─── */

/** Which tier a locked thing needs: "friends" (Thread Friend $3/mo and up) or "members" ($25/mo). */
export type Need = "friends" | "members";
/** True when the role can open something that needs `need`. */
export const hasAccess = (role: Role, need: Need) => (need === "friends" ? can(role, "posts") : can(role, "classes"));

export type JoinCtaCopy = { label: string; short: string; to: string; secondary?: { label: string; to: string } };
/** The one set of join/upgrade words for the whole club. Returns null for members and Chelsea.
    - visitor: "Join the club · $25/mo" (short "Join the club"), secondary "or Thread Friend · $3/mo"
      (left out when `need` is "members", since Thread Friend wouldn't unlock it)
    - friend: "Upgrade to Member · $25/mo" (short "Upgrade")
    `to` already carries ?as=<role>. */
export function joinCta(role: Role, need: Need | "any" = "any"): JoinCtaCopy | null {
  if (role === "visitor") return {
    label: `Join the club · ${PLANS.member.per}`, short: "Join the club", to: withRole(M.join, role),
    secondary: need === "members" ? undefined : { label: `or ${PLANS.friend.name} · ${PLANS.friend.per}`, to: withRole(`${M.join}#mj-tiers`, role) },
  };
  if (role === "friend") return { label: `Upgrade to ${PLANS.member.name} · ${PLANS.member.per}`, short: "Upgrade", to: withRole(M.join, role) };
  return null;
}

/** The join/upgrade button (+ the Thread Friend line for visitors). Renders nothing for members and Chelsea.
    Usage: `<JoinButton need="members" />`, `<JoinButton variant="pop" size="sm" short />`. */
export function JoinButton({ need = "any", variant = "ink", size, short = false, secondary = true, className = "" }: {
  need?: Need | "any"; variant?: "ink" | "pop" | "hero" | "line"; size?: "sm"; short?: boolean; secondary?: boolean; className?: string;
}) {
  const role = useRole();
  const c = joinCta(role, need);
  if (!c) return null;
  return <span className={`mb-join ${className}`}>
    <Link to={c.to} className={`mb-btn mb-btn-${variant}${size ? ` mb-btn-${size}` : ""}`}>{short ? c.short : c.label} <ArrowRight size={16} aria-hidden="true" /></Link>
    {secondary && c.secondary && <Link to={c.secondary.to} className="mb-join-alt">{c.secondary.label}</Link>}
  </span>;
}

/* ─── Locked states ─── */

/** What's behind a lock; picks the reason copy. */
export type LockWhat = "replays" | "classes" | "live" | "posts" | "questions" | "community";
const WHAT: Record<LockWhat, { friend: string; visitor: string }> = {
  replays: { friend: "Class replays", visitor: "every class replay, with chapters, supply lists and patterns" },
  classes: { friend: "Classes and replays", visitor: "every class and replay, with chapters, supply lists and patterns" },
  live: { friend: "Live classes", visitor: "two live sew-alongs a month, with Chelsea answering questions as you go" },
  posts: { friend: "Members-only posts", visitor: "every post from the studio, plus the classes and lives" },
  questions: { friend: "Class questions", visitor: "questions on any class, answered by Chelsea most weekdays" },
  community: { friend: "This part of the club", visitor: "the makes wall, monthly challenges and comments" },
};
/** The one sentence explaining a lock, per role. Usage: `lockReason(role, "members", "live")`. */
export function lockReason(role: Role, need: Need = "members", what: LockWhat = "classes") {
  if (need === "friends") return `This is for ${PLANS.friend.name}s and ${PLANS.member.name}s. ${PLANS.friend.name} is ${PLANS.friend.per} for posts and studio updates; ${PLANS.member.name} is ${PLANS.member.per} for everything.`;
  if (role === "friend") return `${PLANS.friend.name} covers posts and studio updates. ${WHAT[what].friend} come with the ${PLANS.member.name} plan (${PLANS.member.per}), and you can switch anytime.`;
  return `Members get ${WHAT[what].visitor}. ${PLANS.member.per}, cancel anytime.`;
}
/** The handwritten line over a lock. */
export const lockHand = (need: Need) => (need === "friends" ? "for Thread Friends & members" : "this one’s for members");

/** The club's one locked block: a tilted card (lock circle, handwritten note,
    reason, join button, optional second link), centred over a blurred image or
    a blurred peek of the real content. Renders nothing when the role already
    has access. Use LockedRow for one-line locks inside lists.
    Usage:
      <Locked need="members" what="replays" image={c.poster} secondary={{ label: "Browse the library", to: M.classes }} />
      <Locked need="friends" what="posts" peek={<PostPreview />} />
      <Locked what="questions" reason="3 questions from members, with Chelsea’s answers." /> */
export function Locked({ need = "members", what = "classes", reason, hand, image, peek, secondary, className = "", children }: {
  need?: Need; what?: LockWhat; reason?: ReactNode; hand?: string; image?: string; peek?: ReactNode;
  secondary?: { label: string; to: string }; className?: string; children?: ReactNode;
}) {
  const role = useRole();
  if (hasAccess(role, need)) return null;
  const media = image || peek;
  return <div className={`mb-locked${media ? " has-media" : ""}${image ? " has-image" : ""} ${className}`}>
    {image && <img className="mb-locked-media" src={image} alt="" />}
    {peek && <div className="mb-locked-peek" aria-hidden="true" inert>{peek}</div>}
    <div className="mb-locked-card">
      <span className="mb-locked-icon"><Lock size={22} aria-hidden="true" /></span>
      <p className="mb-locked-hand">{hand ?? lockHand(need)}</p>
      <p className="mb-locked-text">{reason ?? lockReason(role, need, what)}</p>
      {children}
      <div className="mb-locked-actions">
        <JoinButton need={need} />
        {secondary && <Link to={withRole(secondary.to, role)} className="mb-locked-link">{secondary.label}</Link>}
      </div>
    </div>
  </div>;
}

/** One-line lock for list rows (a post in a feed, a class in a list).
    Usage: `<LockedRow need={p.visibility === "friends" ? "friends" : "members"} />`. */
export function LockedRow({ need = "members", className = "" }: { need?: Need; className?: string }) {
  const role = useRole();
  const c = joinCta(role, need);
  if (hasAccess(role, need) || !c) return null;
  return <p className={`mb-lockrow ${className}`}>
    <Lock size={14} aria-hidden="true" /> <span>{need === "friends" ? "For Thread Friends and Members." : "For Members."}</span>
    <Link to={c.to}>{c.short} <ArrowRight size={13} aria-hidden="true" /></Link>
  </p>;
}

/* ─── Visibility badge ─── */

/** Who can see a post, in one wording: Everyone / Thread Friends / Members. */
export const VIS_LABEL: Record<Post["visibility"], { label: string; who: string }> = {
  public: { label: "Everyone", who: "Everyone, even visitors" },
  friends: { label: "Thread Friends", who: "Thread Friends and members" },
  members: { label: "Members", who: "Members only" },
};
/** The one visibility pill. Usage: `<VisBadge visibility={post.visibility} />` (add size="sm" in tight rows). */
export function VisBadge({ visibility, size, className = "" }: { visibility: Post["visibility"]; size?: "sm"; className?: string }) {
  const { label, who } = VIS_LABEL[visibility];
  const Icon = visibility === "public" ? Globe : visibility === "friends" ? Heart : Lock;
  return <span className={`mb-vis mb-vis-${visibility}${size ? " mb-vis-sm" : ""} ${className}`} title={`Who can see this: ${who}`}>
    <Icon size={12} aria-hidden="true" fill={visibility === "friends" ? "currentColor" : "none"} /><span className="mb-sr">Visible to </span>{label}
  </span>;
}

/* ─── Review bar extras ─── */

const ReviewSlot = createContext<HTMLElement | null>(null);
/** Page-level review switches (e.g. Live's Before / Live now / Ended) rendered
    inside the bottom review bar instead of on the page. Use plain <button>s with
    aria-pressed; the bar styles them.
    Usage: `<ReviewControls label="Live"><button aria-pressed={s === "before"} onClick={…}>Before</button>…</ReviewControls>` */
export function ReviewControls({ label, children }: { label?: string; children: ReactNode }) {
  const slot = useContext(ReviewSlot);
  if (!slot) return null;
  return createPortal(<div className="mb-review-group" role="group" aria-label={label}>
    {label && <span className="mb-review-label">{label}</span>}{children}
  </div>, slot);
}

/* ─── Sub-nav ─── */

const TABS = [
  { to: M.home, label: "Home", end: true },
  { to: M.posts, label: "Posts" },
  { to: M.classes, label: "Classes" },
  { to: M.live, label: "Live" },
  { to: M.community, label: "Makes" },
  { to: M.account, label: "Settings" },
];
const isOn = (t: (typeof TABS)[number], pathname: string) => (t.end ? pathname === t.to || pathname === `${t.to}/` : pathname.startsWith(t.to));

/** Horizontal tab strip: scrolls the current tab into view and shows edge fades while it overflows. */
function TabStrip({ className, label, role, pathname }: { className: string; label: string; role: Role; pathname: string }) {
  const inner = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ less: false, more: false });
  useLayoutEffect(() => {
    const el = inner.current; if (!el) return;
    const cur = el.querySelector<HTMLElement>('[aria-current="page"]');
    if (cur && el.scrollWidth > el.clientWidth) el.scrollLeft = Math.max(0, cur.offsetLeft - (el.clientWidth - cur.offsetWidth) / 2);
    const check = () => setEdge({ less: el.scrollLeft > 2, more: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check); ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, [pathname]);
  return <nav className={`${className}${edge.less ? " is-less" : ""}${edge.more ? " is-more" : ""}`} aria-label={label}>
    <div ref={inner} className={`${className}-in`}>
      {TABS.map(t => { const on = isOn(t, pathname);
        return <Link key={t.to} to={withRole(t.to, role)} aria-current={on ? "page" : undefined}>{t.label}</Link>; })}
    </div>
  </nav>;
}

/** The header button for the role: Join / Upgrade go to the prototype's join page, members and Chelsea get their account. */
function headerCta(role: Role): NavCta {
  if (role === "visitor") return { label: "Join the club", to: withRole(M.join, role) };
  if (role === "friend") return { label: "Upgrade", to: withRole(M.join, role) };
  const who = role === "chelsea" ? CHELSEA : ME;
  return {
    to: withRole(M.account, role), arrow: false, ariaLabel: "My account",
    label: <>{who.avatar ? <img className="mb-cta-av" src={who.avatar} alt="" /> : <span className="mb-cta-av" aria-hidden="true">{ME.initials}</span>}<span className="mb-cta-txt">My account</span></>,
  };
}

/** Frame for every club page: blue-2 header (tone from `page` via TONE, or `tone`),
    a role-aware header button, the club tabs (sticky strip under the header, plus
    a compact copy inside the header on phones), and the prototype review bar.
    Usage: `<MembersPage page="classes" header={<Header />}>…</MembersPage>` */
export function MembersPage({ tone, page, header, children }: { tone?: Tone; page?: ClubPage; header?: ReactNode; children: ReactNode }) {
  const { pathname, search } = useLocation();
  const [role, setRole] = useState<Role>(() => (ROLES.find(r => r.key === new URLSearchParams(search).get("as"))?.key ?? "member"));
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  const pick = (r: Role) => {
    setRole(r);
    const url = new URL(window.location.href);
    url.searchParams.set("as", r);
    window.history.replaceState(window.history.state, "", url);
  };
  // The footer leaves room for the fixed review bar (its height is published as --mb-review-h).
  useEffect(() => {
    const el = bar.current; if (!el) return;
    const root = document.documentElement;
    const set = () => root.style.setProperty("--mb-review-h", `${Math.ceil(el.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set); ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty("--mb-review-h"); };
  }, []);
  return <RoleContext.Provider value={role}>
    <ReviewSlot.Provider value={slot}>
      <Blue2Page current="club" tone={tone ?? (page ? TONE[page] : "teal")} header={header} className="mb-page" cta={headerCta(role)}
        sub={<TabStrip className="mb-headtabs" label="Club sections" role={role} pathname={pathname} />}>
        <TabStrip className="mb-tabs" label="Club" role={role} pathname={pathname} />
        {children}
      </Blue2Page>
      <div ref={bar} className="mb-review" role="region" aria-label="Prototype review controls">
        <div className="mb-review-roles" role="group" aria-label="Viewing as">
          <span className="mb-review-label">Prototype · sample data · viewing as</span>
          {ROLES.map(r => <button key={r.key} type="button" aria-pressed={r.key === role} onClick={() => pick(r.key)} title={r.note}>{r.label}</button>)}
        </div>
        <div ref={setSlot} className="mb-review-extra" />
      </div>
    </ReviewSlot.Provider>
  </RoleContext.Provider>;
}
